import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  GraduationCap, CheckCircle2, Circle, ChevronRight, RotateCcw, Users,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate } from "../lib/status";

export default function StaffOnboarding() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [view, setView] = useState("me"); // 'me' | 'team'

  const isOwner = user?.account_type === "business_owner" || user?.role === "business_owner"
                    || user?.account_type === "platform_admin";

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/staff-onboarding");
      setMe(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  const loadTeam = useCallback(async () => {
    if (!isOwner) return;
    try {
      const { data } = await api.get("/staff-onboarding/team");
      setTeam(data);
    } catch {
      // Permission denied is fine: non-owners just see their own.
    }
  }, [isOwner]);

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      await Promise.all([loadMe(), loadTeam()]);
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [loadMe, loadTeam]);

  const complete = async (taskKey) => {
    setBusyKey(taskKey);
    try {
      const { data } = await api.post(`/staff-onboarding/${taskKey}/complete`, {});
      setMe(data);
      toast.success("Task marked complete");
      await loadTeam();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  const undo = async (taskKey) => {
    setBusyKey(taskKey);
    try {
      const { data } = await api.post(`/staff-onboarding/${taskKey}/undo`);
      setMe(data);
      await loadTeam();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyKey(null);
    }
  };

  if (loading || !me) {
    return <div className="text-sm text-slate-500" data-testid="onboarding-loading">Loading…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl" data-testid="staff-onboarding-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Training</div>
          <h1 className="font-display text-3xl font-bold mt-1">Staff onboarding</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-xl">
            A short first-day checklist that walks every team member through how to use ConfirmPH safely.
            Mark tasks complete as you learn each one.
          </p>
        </div>
        {isOwner && (
          <div className="flex gap-1 p-1 rounded-xl bg-slate-100" data-testid="onboarding-view-tabs">
            <button
              onClick={() => setView("me")}
              data-testid="onboarding-view-me"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === "me" ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600"}`}
            >My checklist</button>
            <button
              onClick={() => setView("team")}
              data-testid="onboarding-view-team"
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${view === "team" ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600"}`}
            >Team progress</button>
          </div>
        )}
      </div>

      {view === "me" ? (
        <>
          <ProgressBar percent={me.percent} done={me.completed_count} total={me.total} />
          <ul className="space-y-2" data-testid="onboarding-task-list">
            {me.tasks.map((t) => (
              <TaskRow
                key={t.key}
                task={t}
                busy={busyKey === t.key}
                onComplete={() => complete(t.key)}
                onUndo={() => undo(t.key)}
              />
            ))}
          </ul>
        </>
      ) : (
        <TeamView team={team} />
      )}
    </div>
  );
}

function ProgressBar({ percent, done, total }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="onboarding-progress">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-[#1B4332]/10 grid place-items-center text-[#1B4332]">
          <GraduationCap className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="font-semibold">{done} of {total} tasks complete</div>
            <div className="text-2xl font-bold font-mono text-[#1B4332]">{percent}%</div>
          </div>
          <div className="mt-2 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-[#1B4332] transition-all"
              style={{ width: `${percent}%` }}
              data-testid="onboarding-progress-bar"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskRow({ task, busy, onComplete, onUndo }) {
  const done = task.completed;
  return (
    <li
      className={`rounded-xl border ${done ? "border-emerald-200 bg-emerald-50/40" : "border-slate-200 bg-white"} px-4 py-3 flex items-start gap-3`}
      data-testid={`onboarding-task-${task.key}`}
    >
      <button
        type="button"
        onClick={done ? onUndo : onComplete}
        disabled={busy}
        data-testid={`onboarding-task-toggle-${task.key}`}
        className={`mt-0.5 w-6 h-6 rounded-full grid place-items-center border-2 transition flex-shrink-0
                    ${done ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300 text-transparent hover:border-[#1B4332]"}`}
        aria-label={done ? "Mark as not done" : "Mark as done"}
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4 opacity-0" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={`font-medium ${done ? "text-slate-600" : "text-slate-900"}`}>{task.title}</div>
        <p className={`text-sm mt-0.5 ${done ? "text-slate-500" : "text-slate-700"}`}>{task.description}</p>
        <div className="mt-2 flex items-center gap-3 text-xs">
          {task.link && (
            <Link to={task.link} className="text-[#1B4332] font-medium hover:underline inline-flex items-center gap-0.5">
              Open <ChevronRight className="w-3 h-3" />
            </Link>
          )}
          {done && task.completed_at && (
            <span className="text-slate-500">Done {fmtDate(task.completed_at) || task.completed_at.slice(0, 10)}</span>
          )}
          {done && (
            <button
              onClick={onUndo}
              data-testid={`onboarding-task-undo-${task.key}`}
              className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-0.5"
            >
              <RotateCcw className="w-3 h-3" /> Undo
            </button>
          )}
        </div>
      </div>
    </li>
  );
}

function TeamView({ team }) {
  if (!team) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="onboarding-team-empty">
        Team progress is only visible to owners and platform admins.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="onboarding-team-list">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">
        <Users className="w-3.5 h-3.5" /> Team progress ({team.items?.length || 0} members)
      </div>
      {(team.items || []).map((m) => (
        <div
          key={m.user_id}
          className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-center gap-3"
          data-testid={`onboarding-team-row-${m.user_id}`}
        >
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{m.user_name || m.user_email}</div>
            <div className="text-xs text-slate-500">
              {(m.user_role || "staff").replace(/_/g, " ")}
              {m.user_email && <> · {m.user_email}</>}
            </div>
          </div>
          <div className="text-xs text-slate-500 whitespace-nowrap">
            {m.completed_count}/{m.total} tasks
          </div>
          <div className="w-32 sm:w-44 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#1B4332]" style={{ width: `${m.percent}%` }} />
          </div>
          <div className="w-12 text-right font-mono text-sm font-semibold text-[#1B4332]">
            {m.percent}%
          </div>
        </div>
      ))}
      {(team.items || []).length === 0 && (
        <div className="text-sm text-slate-500">No team members yet.</div>
      )}
    </div>
  );
}
